import React from 'react';
import { Link } from 'react-router-dom';
import { AwardIcon, CheckIcon, CopyIcon, FlameIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Seo } from '../../components/Seo';
import { CourseCard } from '../../components/academy/CourseCard';
import { ProgressBar } from '../../components/academy/ProgressBar';
import { JoinDialog } from '../../components/academy/JoinDialog';
import type { Course } from '../../types/academy';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { Photo } from '../../components/ui/Photo';
import { PHOTOS } from '../../data/photos';
import { recommendCourses } from '../../utils/academyRecommend';
import { courseProgress, currentStreak, lessonPath, nextLesson } from '../../utils/academyProgress';
export function AcademyDashboard() {
  const {
    state,
    signOut
  } = useAcademy();
  const {
    hub,
    courses,
    publishedCourses
  } = useAcademyHub();
  const [joinOpen, setJoinOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  if (!state.learner) {
    return <>
        <Seo title="My learning, Cavalla Academy" description="Your enrolled courses and progress." path="/academy/dashboard" noIndex data-id="element-1713" data-mp="17dv6e31tmzqy" />
        <Container className="grid gap-12 py-20 sm:py-28 lg:grid-cols-2 lg:items-center" data-id="element-1714" data-mp="12ftm5esnsudw">
          <div className="max-w-xl" data-id="element-1715" data-mp="eoebmyqqb6rk">
            <h1 className="font-display text-[32px] leading-tight text-ink sm:text-[40px]" data-id="element-1716" data-mp="eph43b3p9h9l">Your learning, in one place</h1>
            <p className="mt-4 text-[17px] text-body" data-id="element-1717" data-mp="of0nqiz8lf5i">
              Create a free account to enrol in courses, keep your progress, take module quizzes and earn certificates.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-1718" data-mp="jmfxfrfjhk6q">
              <Button type="button" size="lg" onClick={() => setJoinOpen(true)} data-id="element-1719" data-mp="3s2j4pw2qp6uj">
                Create a free account
              </Button>
              <Button to="/academy" variant="secondary" size="lg" data-id="element-1720" data-mp="gnqt9u8oq583">
                Browse courses
              </Button>
            </div>
          </div>
          <Photo photo={PHOTOS.ethiopiaPortrait} aspect="aspect-[4/3]" data-id="element-1721" data-mp="ko18zyma4a05" />
        </Container>
        <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={() => setJoinOpen(false)} data-id="element-1722" data-mp="phdnvh15p2fp" />
      </>;
  }
  const learner = state.learner;
  const enrolled = courses.filter(c => state.enrolments[c.slug]).sort((a, b) => (state.lastAccessed[b.slug] ?? '').localeCompare(state.lastAccessed[a.slug] ?? ''));
  const active = enrolled.filter(c => !courseProgress(c, state).complete);
  const current = active[0];
  const others = enrolled.filter(c => c !== current);
  const streak = currentStreak(state.activityDays);
  const certificates = Object.values(state.certificates);
  const lines = new Set(enrolled.map(c => c.serviceLine));
  const suggestions = hub.interests.length ? recommendCourses(hub.interests, state.enrolments, 3).map(slug => publishedCourses.find(c => c.slug === slug)).filter((c): c is Course => Boolean(c)) : [...publishedCourses.filter(c => !state.enrolments[c.slug] && lines.has(c.serviceLine)), ...publishedCourses.filter(c => !state.enrolments[c.slug] && !lines.has(c.serviceLine)).sort((a, b) => Number(Boolean(b.flagship)) - Number(Boolean(a.flagship)))].slice(0, 3);
  const referralUrl = `https://academy.cavalladata.com/join?ref=${learner.referralCode}`;
  const shareText = encodeURIComponent('I am learning with Cavalla Academy, free courses in data, AI and public delivery.');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return <>
      <Seo title="My learning, Cavalla Academy" description="Your enrolled courses and progress." path="/academy/dashboard" noIndex data-id="element-1723" data-mp="d3qok0wrbotd" />
      <Container className="py-12 sm:py-16" data-id="element-1724" data-mp="g7pk2yr7gcs8">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1725" data-mp="g7z84gsow9va">
          <h1 className="font-display text-[30px] leading-tight text-ink sm:text-[38px]" data-id="element-1726" data-mp="l5hk6g20757i">Welcome back, {learner.name.split(' ')[0]}</h1>
          <button type="button" onClick={signOut} className="text-small text-muted underline underline-offset-4 hover:text-ink" data-id="element-1727" data-mp="6906vja97gib">
            Sign out
          </button>
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-1728" data-mp="3tkrj5faljub2">
          <div className="min-w-0" data-id="element-1729" data-mp="28q40fhrd67o">
            {current ? <ContinueCard slug={current.slug} data-id="element-1730" data-mp="3jo69ggnmtv0w" /> : <div className="rounded-md border border-dashed border-line px-6 py-10" data-id="element-1731" data-mp="3jpzk071d8ze6">
                <p className="font-display text-[20px] text-ink" data-id="element-1732" data-mp="3onr9kuejg9f7">{enrolled.length ? 'Every enrolled course is complete' : 'You have not enrolled yet'}</p>
                <p className="mt-2 text-[15px] text-body" data-id="element-1733" data-mp="3eplaslo13jek">Pick a course below to keep going.</p>
              </div>}

            {others.length > 0 && <section aria-labelledby="enrolled" className="mt-12" data-id="element-1734" data-mp="2w2vqmhg33yso">
                <h2 id="enrolled" className="font-display text-[20px] text-ink" data-id="element-1735" data-mp="2r39ao08vg4g6">
                  Your other courses
                </h2>
                <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-1736" data-mp="302cuj6fv4nvq">
                  {others.map(c => {
                const p = courseProgress(c, state);
                const n = nextLesson(c, state);
                return <li key={c.slug} className="grid gap-3 py-4 sm:grid-cols-[1fr_160px_auto] sm:items-center sm:gap-6" data-id="element-1737" data-mp="3a012iy2hbrvu">
                        <Link to={`/academy/courses/${c.slug}`} className="text-[15px] font-medium text-ink hover:text-royal" data-id="element-1738" data-mp="3nu1qqxvt1why">
                          {c.title}
                        </Link>
                        <div className="flex items-center gap-3" data-id="element-1739" data-mp="1imqore0etfv">
                          <ProgressBar percent={p.percent} complete={p.complete} label={`${c.title} progress`} data-id="element-1740" data-mp="3ss3e3v09khz8" />
                          <span className="w-10 shrink-0 text-right text-small text-body" data-id="element-1741" data-mp="2qf72situ1g1k">{p.percent}%</span>
                        </div>
                        {p.complete && state.certificates[c.slug] ? <Link to={`/academy/certificates/${state.certificates[c.slug].id}`} className="text-small text-royal underline underline-offset-4" data-id="element-1742" data-mp="2zczivp7dorq0">
                            Certificate
                          </Link> : <Link to={lessonPath(c.slug, n.moduleIndex, n.lessonIndex)} className="text-small text-royal underline underline-offset-4" data-id="element-1743" data-mp="3e2f7ayjfsl2j">
                            Continue
                          </Link>}
                      </li>;
              })}
                </ul>
              </section>}

            {suggestions.length > 0 && <section aria-labelledby="suggested" className="mt-12" data-id="element-1744" data-mp="1sqwyfx6kqg7a">
                <h2 id="suggested" className="font-display text-[20px] text-ink" data-id="element-1745" data-mp="1l62yepevjipn">
                  Suggested for you
                </h2>
                <div className="mt-4 grid gap-5 md:grid-cols-3" data-id="element-1746" data-mp="1geuntq1t5raw">
                  {suggestions.map(c => <CourseCard key={c.slug} course={c} data-id="element-1747" data-mp="1bgt3l1snblxu" />)}
                </div>
              </section>}
          </div>

          <aside className="space-y-8" data-id="element-1748" data-mp="245qjiy9dx6nf">
            <div className="flex items-center gap-4 border-b border-line pb-8" data-id="element-1749" data-mp="244oz436hb7t1">
              <FlameIcon className={`h-7 w-7 ${streak ? 'text-gold' : 'text-muted'}`} aria-hidden="true" data-id="element-1750" data-mp="1z75gkr2r08va" />
              <div data-id="element-1751" data-mp="1u5pviz0km2cw">
                <p className="font-display text-[28px] leading-none text-ink" data-id="element-1752" data-mp="1u57vrsep9608">
                  {streak} {streak === 1 ? 'day' : 'days'}
                </p>
                <p className="mt-1 text-small text-muted" data-id="element-1753" data-mp="is48pki6ocuj">Learning streak. Complete a lesson each day to keep it going.</p>
              </div>
            </div>

            <section aria-labelledby="certs" className="border-b border-line pb-8" data-id="element-1754" data-mp="esn0wkfab3m7">
              <h2 id="certs" className="text-[15px] font-medium text-ink" data-id="element-1755" data-mp="2dx52ve7g0nfe">
                Certificates
              </h2>
              {certificates.length ? <ul className="mt-3 space-y-2" data-id="element-1756" data-mp="24vdu20hlw0hb">
                  {certificates.map(cert => <li key={cert.id} data-id="element-1757" data-mp="29ugtn5ethehw">
                      <Link to={`/academy/certificates/${cert.id}`} className="flex items-start gap-2 text-[14px] text-body hover:text-ink" data-id="element-1758" data-mp="29va1k9piia1m">
                        <AwardIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1759" data-mp="31kuykc4urghf" />
                        {courses.find(c => c.slug === cert.courseSlug)?.title}
                      </Link>
                    </li>)}
                </ul> : <p className="mt-2 text-[14px] text-muted" data-id="element-1760" data-mp="3ghcd1c8c9b87">Finish every module of a course to earn its certificate.</p>}
            </section>

            <section aria-labelledby="referral" data-id="element-1761" data-mp="3laoy5cdt6zw7">
              <h2 id="referral" className="text-[15px] font-medium text-ink" data-id="element-1762" data-mp="3ummbqkkelgy1">
                Invite a colleague
              </h2>
              <p className="mt-1 text-[14px] text-body" data-id="element-1763" data-mp="3qlkajy6xeutf">
                You earn the Connector badge when someone you invite enrols in their first course.
              </p>
              <div className="mt-4 flex items-stretch overflow-hidden rounded-sm border border-line bg-charcoal" data-id="element-1764" data-mp="39rq8kwiq5d1">
                <input readOnly value={referralUrl} aria-label="Your referral link" className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-small text-body focus:outline-none" onFocus={e => e.currentTarget.select()} data-id="element-1765" data-mp="3bp1y69n3a4sq" />
                <button type="button" onClick={copy} className="flex shrink-0 items-center gap-1.5 border-l border-line px-3 text-small text-ink transition-colors duration-150 hover:bg-panel" data-id="element-1766" data-mp="3gpzf6iwd4ssn">
                  {copied ? <CheckIcon className="h-4 w-4 text-[#4ADE80]" aria-hidden="true" data-id="element-1767" data-mp="2jw27b7ze7q2l" /> : <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-1768" data-mp="2ex8qp6ot7iy7" />}
                  <span aria-live="polite" data-id="element-1769" data-mp="2a6sbpi5cbp9q">{copied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-small" data-id="element-1770" data-mp="2588mn8vg9xd5">
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralUrl)}`} target="_blank" rel="noopener noreferrer" className="text-royal underline underline-offset-4 hover:text-ink" data-id="element-1771" data-mp="215u6iwj61bpk">
                  LinkedIn
                </a>
                <a href={`https://x.com/intent/post?text=${shareText}&url=${encodeURIComponent(referralUrl)}`} target="_blank" rel="noopener noreferrer" className="text-royal underline underline-offset-4 hover:text-ink" data-id="element-1772" data-mp="1mao048g5ravp">
                  X
                </a>
                <a href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(referralUrl)}`} target="_blank" rel="noopener noreferrer" className="text-royal underline underline-offset-4 hover:text-ink" data-id="element-1773" data-mp="3t4yc2qtlcn8s">
                  WhatsApp
                </a>
                <a href={`mailto:?subject=${encodeURIComponent('Free courses from Cavalla Academy')}&body=${shareText}%20${encodeURIComponent(referralUrl)}`} className="text-royal underline underline-offset-4 hover:text-ink" data-id="element-1774" data-mp="366lgax5ffvv2">
                  Email
                </a>
              </div>
            </section>
          </aside>
        </div>
      </Container>
    </>;
}
function ContinueCard({
  slug
}: {
  slug: string;
}) {
  const {
    state
  } = useAcademy();
  const {
    courses
  } = useAcademyHub();
  const course = courses.find(c => c.slug === slug);
  if (!course) return null;
  const p = courseProgress(course, state);
  const n = nextLesson(course, state);
  return <section aria-labelledby="continue" className="rounded-md border border-line bg-panel p-6 sm:p-8" data-id="element-1775" data-mp="4teyecwbylzd">
      <p className="text-small text-muted" data-id="element-1776" data-mp="jm8rj14iz0mi">Pick up where you left off</p>
      <h2 id="continue" className="mt-2 font-display text-[24px] leading-snug text-ink sm:text-[28px]" data-id="element-1777" data-mp="eq1hox90d4za">
        {course.title}
      </h2>
      <p className="mt-3 text-[15px] text-body" data-id="element-1778" data-mp="3i68o3bit5gbr">
        Next: Module {n.moduleIndex + 1}, {course.modules[n.moduleIndex].lessons[n.lessonIndex]}
      </p>
      <div className="mt-6 flex items-center gap-4" data-id="element-1779" data-mp="ra89o53c7hh">
        <ProgressBar percent={p.percent} complete={p.complete} label="Course progress" data-id="element-1780" data-mp="3s4od98y9yfim" />
        <span className="shrink-0 text-[15px] text-ink" data-id="element-1781" data-mp="34eu1kupoywzy">{p.percent}%</span>
      </div>
      <p className="mt-2 text-small text-muted" data-id="element-1782" data-mp="34chclxlinmde">
        {p.completedModules} of {p.totalModules} modules · {p.completedLessons} of {p.totalLessons} lessons
      </p>
      <Button to={lessonPath(course.slug, n.moduleIndex, n.lessonIndex)} size="lg" className="mt-7" data-id="element-1783" data-mp="1ew3x9odoav5q">
        Continue
      </Button>
    </section>;
}